import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Send, ChevronDown, RefreshCw, X, Paperclip } from "lucide-react";
import ChatBubble from "./ChatBubble";
import TypingIndicator from "./TypingIndicator";
import ToolRecommendationCards, { type ToolRecommendation } from "./ToolRecommendationCards";
import AttachmentChips from "./AttachmentChips";
import { supabase } from "@/integrations/supabase/client";
import { useBuilderConversation } from "@/hooks/useBuilderConversation";
import { useBuilderAttachments } from "@/hooks/useBuilderAttachments";
import {
  BuilderMessage,
  generateMessageId,
  getInitialAssistantMessage,
  isConversationComplete,
  extractFinalPrompt,
  detectCurrentStep,
  appendConversationContext,
  type GuidedBuilderResponse,
  type StepSelection,
  TOOL_SELECT_PREFIX,
} from "@/lib/promptBuilderUtils";
import { classifyError, type ClassifiedError } from "@/lib/fetchWithTimeout";
import type { DraftResume, RemixContext } from "@/types/promptBuilder";
import OptionButtons from "./OptionButtons";
import { toast } from "sonner";

interface PromptBuilderChatProps {
  initialGoal?: string;
  prefillInput?: string;
  deferInitialSend?: boolean;
  onComplete: (finalPrompt: string, conversationId?: string | null) => Promise<void>;
  onClose: () => void;
  inline?: boolean;
  remixContext?: RemixContext;
  draftResume?: DraftResume;
  guided?: boolean;
  onStepChange?: (step: number) => void;
  onMessagesChange?: (messages: BuilderMessage[]) => void;
  existingConversationId?: string | null;
}

const PromptBuilderChat = ({
  initialGoal,
  prefillInput,
  deferInitialSend = false,
  onComplete,
  onClose,
  inline = false,
  remixContext,
  draftResume,
  guided = false,
  onStepChange,
  onMessagesChange,
  existingConversationId,
}: PromptBuilderChatProps) => {
  const [messages, setMessages] = useState<BuilderMessage[]>([]);
  const [input, setInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
  const [showScrollIndicator, setShowScrollIndicator] = useState(false);
  const [retryPayload, setRetryPayload] = useState<{
    type: 'builder' | 'scoring';
    messages: BuilderMessage[];
    prompt?: string;
    conversationId?: string | null;
    selections?: StepSelection[];
  } | null>(null);
  const [errorInfo, setErrorInfo] = useState<ClassifiedError | null>(null);

  // Guided mode state
  const [selections, setSelections] = useState<StepSelection[]>([]);
  const [writeOwnExpanded, setWriteOwnExpanded] = useState(false);
  const [writeOwnValue, setWriteOwnValue] = useState("");
  const [latestOptions, setLatestOptions] = useState<string[]>([]);

  // Tool recommendation state
  const [toolRecommendations, setToolRecommendations] = useState<ToolRecommendation[] | null>(null);
  const [isLoadingTools, setIsLoadingTools] = useState(false);
  const [isOptimizingForTool, setIsOptimizingForTool] = useState(false);
  const [toolRecommendationId, setToolRecommendationId] = useState<string | null>(null);
  const pendingPromptRef = useRef<{ prompt: string; conversationId?: string | null } | null>(null);

  // Conversation persistence hook
  const { conversationId, startConversation, resumeConversation, updateConversation, completeConversation, abandonConversation } =
    useBuilderConversation();

  // File attachments
  const { attachments, attachmentIds, uploadFile, removeAttachment, loadAttachments, acceptString } = useBuilderAttachments();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const prevMessageCount = useRef(0);
  const maxStepReachedRef = useRef<number>(0);
  const confirmedStepRef = useRef<number>(0);

  // Detect scroll position for indicator
  const handleScroll = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const threshold = 100;
    const isNearBottom = container.scrollTop + container.clientHeight >= container.scrollHeight - threshold;

    setShowScrollIndicator(!isNearBottom);
  }, []);

  // Scroll to bottom
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  // Check scroll indicator on content changes
  useEffect(() => {
    handleScroll();
  }, [messages, isTyping, handleScroll]);

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  // Auto-scroll only when NEW messages arrive (not on initial render)
  useEffect(() => {
    if (messages.length > prevMessageCount.current && prevMessageCount.current > 0) {
      scrollToBottom();
    }
    prevMessageCount.current = messages.length;
  }, [messages, scrollToBottom]);

  // Mobile keyboard handling - keep input visible when keyboard appears
  useEffect(() => {
    const handleResize = () => {
      if (!window.visualViewport) return;

      const viewportHeight = window.visualViewport.height;
      const windowHeight = window.innerHeight;

      // Keyboard is likely open if viewport is significantly smaller
      const keyboardOpen = windowHeight - viewportHeight > 150;

      if (keyboardOpen && inputRef.current) {
        // Small delay to let layout settle, then scroll input into view
        setTimeout(() => {
          inputRef.current?.scrollIntoView({ block: "center", behavior: "smooth" });
        }, 100);
      }
    };

    window.visualViewport?.addEventListener("resize", handleResize);
    return () => window.visualViewport?.removeEventListener("resize", handleResize);
  }, []);

  // Scroll when typing indicator appears
  useEffect(() => {
    if (isTyping) {
      scrollToBottom();
    }
  }, [isTyping, scrollToBottom]);

  // Initialize conversation
  useEffect(() => {
    if (isInitialized) return;
    setIsInitialized(true);

    if (draftResume) {
      // RESUME MODE: restore saved conversation
      resumeConversation(draftResume.conversationId);
      loadAttachments(draftResume.conversationId);

      const hasMessages = draftResume.messages.length > 0;

      if (hasMessages) {
        // Path A: Draft has saved messages — restore them
        const restoredMessages: BuilderMessage[] = draftResume.messages.map((m) => ({
          id: m.id || generateMessageId(),
          role: m.role as 'user' | 'assistant',
          content: m.content,
          timestamp: m.timestamp ? new Date(m.timestamp) : new Date(),
        }));
        setMessages(restoredMessages);
        // Detect max step from restored messages
        const lastAssistant = [...restoredMessages].reverse().find((m) => m.role === 'assistant');
        if (lastAssistant) {
          const step = detectCurrentStep(lastAssistant.content);
          let detected = 0;
          switch (step) {
            case 'persona': detected = 1; break;
            case 'context': detected = 2; break;
            case 'task': case 'sources': detected = 3; break;
            case 'output_format': detected = 4; break;
          }
          maxStepReachedRef.current = detected;
          confirmedStepRef.current = detected;
        }
      } else if (draftResume.initialGoal) {
        // Path B: Draft only has initial_goal but no messages — re-send the goal
        const userMessage: BuilderMessage = {
          id: generateMessageId(),
          role: "user",
          content: draftResume.initialGoal,
          timestamp: new Date(),
        };
        setMessages([userMessage]);
        sendToBuilder([userMessage]);
      } else {
        // Path C: Draft has nothing useful — show default greeting
        setMessages([getInitialAssistantMessage()]);
      }
    } else if (guided) {
      // GUIDED MODE initialization
      if (existingConversationId) {
        resumeConversation(existingConversationId);
      } else {
        startConversation((prefillInput || initialGoal)?.trim() || undefined);
      }

      if (initialGoal && initialGoal.trim() && !remixContext) {
        // Has initial goal: skip Step 0, set it as first selection
        const initSelections: StepSelection[] = [{ step: 0, value: initialGoal.trim() }];
        setSelections(initSelections);
        const userMessage: BuilderMessage = {
          id: generateMessageId(),
          role: "user",
          content: initialGoal.trim(),
          timestamp: new Date(),
        };
        setMessages([userMessage]);
        sendToGuidedBuilder([userMessage], initSelections);
      } else {
        // No initial goal or remix: call AI for Step 0 greeting + goal buttons
        setMessages([]);
        sendToGuidedBuilder([], [], remixContext);
      }
    } else {
      // Start new conversation persistence
      if (existingConversationId) {
        resumeConversation(existingConversationId);
        loadAttachments(existingConversationId);
      } else {
        startConversation((prefillInput || initialGoal)?.trim() || undefined);
      }

      if (remixContext) {
        // REMIX MODE: Call AI to generate remix opener (takes priority over initialGoal)
        setMessages([]);
        sendToBuilder([]);
      } else if (deferInitialSend) {
        // Onboarding deferred mode: prefill composer and wait for explicit user send.
        setMessages([getInitialAssistantMessage()]);
        if (prefillInput && prefillInput.trim()) {
          setInput(prefillInput);
        } else if (initialGoal && initialGoal.trim()) {
          setInput(initialGoal);
        }
      } else if (initialGoal && initialGoal.trim()) {
        // Normal mode with initial goal
        const userMessage: BuilderMessage = {
          id: generateMessageId(),
          role: "user",
          content: initialGoal,
          timestamp: new Date(),
        };
        setMessages([userMessage]);
        sendToBuilder([userMessage]);
      } else {
        // Default mode: show standard kickoff
        setMessages([getInitialAssistantMessage()]);
      }
    }
  }, [initialGoal, prefillInput, deferInitialSend, isInitialized, startConversation, resumeConversation, remixContext, draftResume, guided, existingConversationId, loadAttachments]);

  const sendToBuilder = async (conversationHistory: BuilderMessage[]) => {
    setIsTyping(true);
    setErrorInfo(null);
    setRetryPayload(null);

    try {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 300_000);

      let data: { content: string } | null = null;
      let error: unknown = null;
      try {
        const result = await supabase.functions.invoke("prompt-builder", {
          body: {
            messages: conversationHistory.map((m) => ({
              role: m.role,
              content: m.content,
            })),
            remixContext: remixContext
              ? { originalPrompt: remixContext.originalPrompt }
              : undefined,
            current_step: confirmedStepRef.current,
            attachment_ids: attachmentIds.length > 0 ? attachmentIds : undefined,
          },
        });
        data = result.data as { content: string } | null;
        error = result.error;
      } finally {
        clearTimeout(timer);
      }

      if (error) {
        console.error("Error calling prompt-builder:", error);
        const classified = classifyError(error);
        setErrorInfo(classified);
        setRetryPayload({ type: 'builder', messages: conversationHistory });
        return;
      }

      if (!data?.content) {
        setErrorInfo({
          kind: "unknown",
          message: "Prompt builder returned an empty response. Please try again.",
          retryable: true,
        });
        setRetryPayload({ type: 'builder', messages: conversationHistory });
        return;
      }

      const tempMessage: BuilderMessage = {
        id: "temp",
        role: "assistant",
        content: data.content,
        timestamp: new Date(),
      };

      console.log("[PromptBuilder] AI response received, checking completion...");
      console.log("[PromptBuilder] Response preview:", data.content.substring(0, 300));

      const isComplete = isConversationComplete([...conversationHistory, tempMessage]);
      console.log("[PromptBuilder] Is conversation complete?", isComplete);

      if (isComplete) {
        const cleanPrompt = extractFinalPrompt(data.content);
        console.log("[PromptBuilder] Extracted prompt:", cleanPrompt.substring(0, 200));

        setIsTransitioning(true);
        const transitionMessage: BuilderMessage = {
          id: generateMessageId(),
          role: "assistant",
          content:
            "Great, that's everything I need! Let me find the best AI tools for your prompt...",
          timestamp: new Date(),
        };
        const finalMessages = [...conversationHistory, transitionMessage];
        setMessages(finalMessages);

        // Complete conversation persistence before tool rec
        await completeConversation(finalMessages, cleanPrompt);

        // Store pending prompt and fetch tool recommendations
        const enrichedPrompt = appendConversationContext(cleanPrompt, conversationHistory);
        pendingPromptRef.current = { prompt: enrichedPrompt, conversationId };
        await fetchToolRecommendations(cleanPrompt, finalMessages);
      } else {
        const assistantMessage: BuilderMessage = {
          id: generateMessageId(),
          role: "assistant",
          content: data.content,
          timestamp: new Date(),
        };
        const updatedMessages = [...conversationHistory, assistantMessage];
        setMessages(updatedMessages);

        // Detect current step and enforce forward-only progression
        const step = detectCurrentStep(data.content);
        let detected = 0;
        switch (step) {
          case "persona":
            detected = 1;
            break;
          case "context":
            detected = 2;
            break;
          case "task":
          case "sources":
            detected = 3;
            break;
          case "output_format":
            detected = 4;
            break;
        }
        
        // Only advance if the detected step is exactly the next expected step
        if (detected === confirmedStepRef.current + 1) {
          confirmedStepRef.current = detected;
          maxStepReachedRef.current = detected;
        } else if (detected > 0 && detected !== confirmedStepRef.current + 1) {
          // AI regressed or skipped — keep step unchanged, enforcement will self-correct
          console.warn(`[PromptBuilder] Step enforcement: expected ${confirmedStepRef.current + 1}, got ${detected}. Keeping step at ${confirmedStepRef.current}.`);
        }
        
        updateConversation(updatedMessages, maxStepReachedRef.current);
      }
    } catch (err) {
      console.error("Error in sendToBuilder:", err);
      const classified = classifyError(err);
      setErrorInfo(classified);
      setRetryPayload({ type: 'builder', messages: conversationHistory });
    } finally {
      setIsTyping(false);
    }
  };

  // Guided builder API call
  const sendToGuidedBuilder = async (
    conversationHistory: BuilderMessage[],
    currentSelections: StepSelection[],
    remixCtx?: RemixContext,
  ) => {
    setIsTyping(true);
    setErrorInfo(null);
    setRetryPayload(null);
    setLatestOptions([]);

    try {
      let data: GuidedBuilderResponse | null = null;
      let error: unknown = null;

      try {
        const result = await supabase.functions.invoke("prompt-builder-guided", {
          body: {
            messages: conversationHistory.map((m) => ({
              role: m.role,
              content: m.content,
            })),
            selections: currentSelections,
            remixContext: (remixCtx || remixContext)
              ? { originalPrompt: (remixCtx || remixContext)!.originalPrompt }
              : undefined,
            attachment_ids: attachmentIds.length > 0 ? attachmentIds : undefined,
          },
        });
        data = result.data as GuidedBuilderResponse | null;
        error = result.error;
      } catch (e) {
        error = e;
      }

      if (error) {
        console.error("Error calling prompt-builder-guided:", error);
        const classified = classifyError(error);
        setErrorInfo(classified);
        setRetryPayload({ type: 'builder', messages: conversationHistory, selections: currentSelections });
        return;
      }

      if (!data?.message) {
        setErrorInfo({
          kind: "unknown",
          message: "Guided builder returned an empty response. Please try again.",
          retryable: true,
        });
        setRetryPayload({ type: 'builder', messages: conversationHistory, selections: currentSelections });
        return;
      }

      console.log("[GuidedBuilder] Response:", { step: data.step, optionsCount: data.options?.length, hasFinalPrompt: !!data.final_prompt });

      const isComplete = data.step === 'complete' || data.step === 5 || (data.step as any) === '5';
      if (isComplete && data.final_prompt) {
        // Conversation complete
        setIsTransitioning(true);
        const transitionMessage: BuilderMessage = {
          id: generateMessageId(),
          role: "assistant",
          content: "Great, that's everything I need! Let me find the best AI tools for your prompt...",
          timestamp: new Date(),
        };
        const finalMessages = [...conversationHistory, transitionMessage];
        setMessages(finalMessages);
        maxStepReachedRef.current = 5;

        await completeConversation(finalMessages, data.final_prompt);

        // Store pending prompt and fetch tool recommendations
        const enrichedPrompt = appendConversationContext(data.final_prompt, conversationHistory);
        pendingPromptRef.current = { prompt: enrichedPrompt, conversationId };
        await fetchToolRecommendations(data.final_prompt, finalMessages);
      } else {
        // Normal step — show message + options
        const assistantMessage: BuilderMessage = {
          id: generateMessageId(),
          role: "assistant",
          content: data.message,
          timestamp: new Date(),
        };
        const updatedMessages = [...conversationHistory, assistantMessage];
        setMessages(updatedMessages);
        setLatestOptions(data.options || []);

        // Update step tracking
        const stepNum = typeof data.step === 'number' ? data.step : 0;
        maxStepReachedRef.current = Math.max(maxStepReachedRef.current, stepNum);
        updateConversation(updatedMessages, maxStepReachedRef.current);
      }
    } catch (err) {
      console.error("Error in sendToGuidedBuilder:", err);
      const classified = classifyError(err);
      setErrorInfo(classified);
      setRetryPayload({ type: 'builder', messages: conversationHistory, selections: currentSelections });
    } finally {
      setIsTyping(false);
    }
  };

  // Wrapper that catches scoring errors and shows retry UI instead of ejecting
  const submitForScoring = async (prompt: string, convId?: string | null) => {
    try {
      await onComplete(prompt, convId);
    } catch (err) {
      const classified = classifyError(err);
      setIsTransitioning(false);
      setIsOptimizingForTool(false);
      setErrorInfo(classified);
      setRetryPayload({ type: 'scoring', messages, prompt, conversationId: convId });
    }
  };

  // Fetch tool recommendations after builder completes
  const fetchToolRecommendations = async (finalPrompt: string, currentMessages: BuilderMessage[]) => {
    setIsLoadingTools(true);
    try {
      const { data, error } = await supabase.functions.invoke("recommend-tools-inline-v6", {
        body: {
          final_prompt: finalPrompt,
          conversation_id: conversationId || undefined,
        },
      });

      if (error || !data?.recommendations) {
        console.error("Tool recommendation failed:", error);
        // Fall through to scoring without tool rec
        setIsTransitioning(false);
        const skipMessage: BuilderMessage = {
          id: generateMessageId(),
          role: "assistant",
          content: "I'm now scoring your prompt and will provide suggestions to make it even more effective. Just a moment...",
          timestamp: new Date(),
        };
        setMessages([...currentMessages, skipMessage]);
        if (pendingPromptRef.current) {
          await submitForScoring(pendingPromptRef.current.prompt, pendingPromptRef.current.conversationId);
        }
        return;
      }

      setToolRecommendationId(data.recommendation_id);
      setToolRecommendations(data.recommendations);
      setIsTransitioning(false);

      const recMessage: BuilderMessage = {
        id: generateMessageId(),
        role: "assistant",
        content: "Here are the best AI tools for your prompt. Select one and I'll optimize your prompt specifically for that platform:",
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, recMessage]);
    } catch (err) {
      console.error("Tool recommendation error:", err);
      // Fall through to scoring
      setIsTransitioning(false);
      if (pendingPromptRef.current) {
        await submitForScoring(pendingPromptRef.current.prompt, pendingPromptRef.current.conversationId);
      }
    } finally {
      setIsLoadingTools(false);
    }
  };

  // Handle user selecting a tool and optimizing
  const handleToolSelection = async (rec: ToolRecommendation) => {
    if (!pendingPromptRef.current) return;
    setIsOptimizingForTool(true);

    const selectMessage: BuilderMessage = {
      id: generateMessageId(),
      role: "user",
      content: `${TOOL_SELECT_PREFIX}${rec.tool} (${rec.model})`,
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, selectMessage]);

    try {
      // Split off the conversation transcript so the optimizer only reformats the prompt
      const transcriptSeparator = '\n\n---\n## Additional Context';
      const fullPrompt = pendingPromptRef.current.prompt;
      const separatorIndex = fullPrompt.indexOf(transcriptSeparator);
      const promptOnly = separatorIndex >= 0 ? fullPrompt.substring(0, separatorIndex) : fullPrompt;
      const transcriptSuffix = separatorIndex >= 0 ? fullPrompt.substring(separatorIndex) : '';

      const { data, error } = await supabase.functions.invoke("optimize-prompt-for-tool-v6", {
        body: {
          final_prompt: promptOnly,
          selected_tool: rec.tool,
          selected_model: rec.model,
          recommendation_id: toolRecommendationId,
        },
      });

      if (error || !data?.optimized_prompt) {
        console.error("Optimization failed:", error);
        // Use original prompt
        const fallbackMsg: BuilderMessage = {
          id: generateMessageId(),
          role: "assistant",
          content: "I couldn't optimize for that tool, but your prompt is already great! Scoring now...",
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, fallbackMsg]);
        await submitForScoring(pendingPromptRef.current!.prompt, pendingPromptRef.current!.conversationId);
        return;
      }

      const optimizedMsg: BuilderMessage = {
        id: generateMessageId(),
        role: "assistant",
        content: `Your prompt has been optimized for ${rec.tool}! Finalizing now...`,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, optimizedMsg]);
      setToolRecommendations(null);

      // Re-append the original conversation transcript to the optimized prompt
      const enrichedOptimized = data.optimized_prompt + transcriptSuffix;
      await submitForScoring(enrichedOptimized, pendingPromptRef.current.conversationId);
    } catch (err) {
      console.error("Tool optimization error:", err);
      // Fall through with original
      await submitForScoring(pendingPromptRef.current!.prompt, pendingPromptRef.current!.conversationId);
    } finally {
      setIsOptimizingForTool(false);
    }
  };

  const handleOptionSelect = (value: string) => {
    const userMessage: BuilderMessage = {
      id: generateMessageId(),
      role: "user",
      content: value,
      timestamp: new Date(),
    };
    const newMessages = [...messages, userMessage];
    setMessages(newMessages);

    const newSelections = [...selections, { step: selections.length, value }];
    setSelections(newSelections);
    setLatestOptions([]);
    setWriteOwnExpanded(false);
    setWriteOwnValue("");

    sendToGuidedBuilder(newMessages, newSelections);
  };

  // Guided mode: handle "Write my own" submit
  const handleWriteOwnSubmit = () => {
    if (!writeOwnValue.trim()) return;
    handleOptionSelect(writeOwnValue.trim());
  };

  // Guided mode: handle edit (pencil) on previous user message
  const handleGuidedEdit = (messageId: string) => {
    const message = messages.find((m) => m.id === messageId);
    if (!message || message.role !== "user") return;

    // Find the index of this user message and determine which step it corresponds to
    const userMessages = messages.filter((m) => m.role === "user");
    const userIndex = userMessages.findIndex((m) => m.id === messageId);
    if (userIndex === -1) return;

    // Truncate messages to before this user message
    const msgIndex = messages.findIndex((m) => m.id === messageId);
    const truncatedMessages = messages.slice(0, msgIndex);
    setMessages(truncatedMessages);

    // Truncate selections to before this step
    const truncatedSelections = selections.slice(0, userIndex);
    setSelections(truncatedSelections);

    // Show "Write my own" expanded with old value pre-filled
    setLatestOptions([]);
    setWriteOwnExpanded(true);
    setWriteOwnValue(message.content);

    // Re-fetch options for this step
    sendToGuidedBuilder(truncatedMessages, truncatedSelections);
  };

  const handleRetry = async () => {
    if (!retryPayload) return;
    setErrorInfo(null);
    if (retryPayload.type === 'builder') {
      if (guided && retryPayload.selections) {
        sendToGuidedBuilder(retryPayload.messages, retryPayload.selections);
      } else {
        sendToBuilder(retryPayload.messages);
      }
    } else if (retryPayload.type === 'scoring' && retryPayload.prompt) {
      setIsTransitioning(true);
      setRetryPayload(null);
      setErrorInfo(null);
      await submitForScoring(retryPayload.prompt, retryPayload.conversationId);
    }
  };

  const handleCancelError = () => {
    setErrorInfo(null);
    setRetryPayload(null);
    setIsTransitioning(false);
  };

  // File attachment handler
  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || !conversationId) return;

    for (const file of Array.from(files)) {
      try {
        await uploadFile(file, conversationId);
      } catch (err) {
        console.error('[PromptBuilderChat] File upload error:', err);
        toast.error(err instanceof Error ? err.message : 'File upload failed');
      }
    }

    // Reset input so the same file can be re-selected
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Auto-grow textarea handler
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const textarea = e.target;
    setInput(textarea.value);

    // Reset height to auto to get the correct scrollHeight
    textarea.style.height = "auto";
    // Set to scrollHeight, capped at max height (120px)
    const maxHeight = 120;
    textarea.style.height = `${Math.min(textarea.scrollHeight, maxHeight)}px`;
  };

  const handleSend = () => {
    if (!input.trim() || isTyping) return;

    // Reset textarea height after sending
    if (inputRef.current) {
      inputRef.current.style.height = "auto";
    }

    if (editingMessageId) {
      const editIndex = messages.findIndex((m) => m.id === editingMessageId);
      if (editIndex !== -1) {
        const truncatedMessages = messages.slice(0, editIndex);

        const updatedMessage: BuilderMessage = {
          id: generateMessageId(),
          role: "user",
          content: input.trim(),
          timestamp: new Date(),
        };

        const newMessages = [...truncatedMessages, updatedMessage];
        setMessages(newMessages);
        setInput("");
        setEditingMessageId(null);

        sendToBuilder(newMessages);
      }
    } else {
      const userMessage: BuilderMessage = {
        id: generateMessageId(),
        role: "user",
        content: input.trim(),
        timestamp: new Date(),
      };

      const newMessages = [...messages, userMessage];
      setMessages(newMessages);
      setInput("");
      sendToBuilder(newMessages);
    }
  };

  const handleEdit = (messageId: string) => {
    const message = messages.find((m) => m.id === messageId);
    if (message && message.role === "user") {
      setEditingMessageId(messageId);
      setInput(message.content);
      inputRef.current?.focus();
    }
  };

  const handleCancelEdit = () => {
    setEditingMessageId(null);
    setInput("");
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
    if (e.key === "Escape" && editingMessageId) {
      handleCancelEdit();
    }
  };

  // Compute current step number (forward-only - never goes backwards)
  const currentStepNumber = useMemo(() => {
    if (guided) {
      // In guided mode, step comes directly from selections length
      return maxStepReachedRef.current;
    }

    const lastAssistantMsg = [...messages].reverse().find((m) => m.role === "assistant");
    if (!lastAssistantMsg) return maxStepReachedRef.current;

    const step = detectCurrentStep(lastAssistantMsg.content);
    let detected = 0;
    switch (step) {
      case "persona":
        detected = 1;
        break;
      case "context":
        detected = 2;
        break;
      case "task":
      case "sources":
        detected = 3;
        break;
      case "output_format":
        detected = 4;
        break;
    }

    // Never go backwards - use highest step reached
    maxStepReachedRef.current = Math.max(maxStepReachedRef.current, detected);
    return maxStepReachedRef.current;
  }, [messages, guided]);

  // Notify parent of step changes
  useEffect(() => {
    onStepChange?.(currentStepNumber);
  }, [currentStepNumber, onStepChange]);

  // Notify parent of message changes
  useEffect(() => {
    onMessagesChange?.(messages);
  }, [messages, onMessagesChange]);

  return (
    <div className="flex flex-1 min-h-0 bg-background">
      {/* Main chat area */}
      <div className="flex-1 flex flex-col min-w-0 min-h-0">
        {/* Header - hidden in inline mode since parent card has header */}
        {!inline && (
          <div className="px-4 py-3 border-b border-border flex-shrink-0">
            <h2 className="font-semibold text-foreground">Build a Prompt</h2>
            {/* Mobile progress indicator */}
            <div className="md:hidden flex items-center gap-2 mt-2">
              {[1, 2, 3, 4].map((step) => (
                <div
                  key={step}
                  className={`h-1.5 flex-1 rounded-full ${step <= currentStepNumber ? "bg-primary" : "bg-muted"}`}
                />
              ))}
            </div>
          </div>
        )}

        {/* SCROLLABLE: ALL messages + suggestions + indicators */}
        <div
          ref={messagesContainerRef}
          className="flex-1 overflow-y-auto overscroll-contain px-4 py-4 space-y-4 min-h-0 basis-0"
          onScroll={handleScroll}
        >
          {/* ALL messages in chronological order */}
          {messages.map((message) => (
            <div key={message.id}>
              <ChatBubble
                role={message.role}
                content={message.content}
                isEditable={message.role === "user" && !isTyping}
                onEdit={() => guided ? handleGuidedEdit(message.id) : handleEdit(message.id)}
              />
            </div>
          ))}

          {/* Guided mode: Attachments + Option buttons */}
          {guided && !isTransitioning && (
            <div className="px-4 pb-2">
              <AttachmentChips attachments={attachments} onRemove={removeAttachment} />
              {conversationId && (
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isTyping}
                  className="mt-1 inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
                >
                  <Paperclip className="h-3.5 w-3.5" />
                  Attach a file
                </button>
              )}
            </div>
          )}
          {guided && latestOptions.length > 0 && !isTyping && !isTransitioning && (
            <OptionButtons
              options={latestOptions}
              onSelect={handleOptionSelect}
              disabled={isTyping || isTransitioning}
              isWriteOwnExpanded={writeOwnExpanded}
              onExpandWriteOwn={() => setWriteOwnExpanded(true)}
              writeOwnValue={writeOwnValue}
              onWriteOwnChange={setWriteOwnValue}
              onWriteOwnSubmit={handleWriteOwnSubmit}
              onCollapseWriteOwn={() => { setWriteOwnExpanded(false); setWriteOwnValue(""); }}
            />
          )}

          {/* Tool Recommendation Cards */}
          {toolRecommendations && !isLoadingTools && !isOptimizingForTool && (
            <ToolRecommendationCards
              recommendations={toolRecommendations}
              onSelect={handleToolSelection}
              onSkip={async () => {
                if (pendingPromptRef.current) {
                  await submitForScoring(pendingPromptRef.current.prompt, pendingPromptRef.current.conversationId);
                }
              }}
              isOptimizing={isOptimizingForTool}
            />
          )}

          {/* Typing / loading indicator (single bubble for any pending state) */}
          {(isTyping || isTransitioning || isLoadingTools || isOptimizingForTool) && !errorInfo && (
            <TypingIndicator />
          )}

          {/* Error with retry/cancel */}
          {errorInfo && (
            <div className="flex flex-col items-start gap-2 p-3 rounded-lg border border-destructive/30 bg-destructive/5">
              <p className="text-sm text-destructive">{errorInfo.message}</p>
              <div className="flex gap-2">
                {errorInfo.retryable && (
                  <Button variant="outline" size="sm" onClick={handleRetry}>
                    <RefreshCw className="h-3.5 w-3.5 mr-1" />
                    Retry
                  </Button>
                )}
                <Button variant="ghost" size="sm" onClick={handleCancelError}>
                  <X className="h-3.5 w-3.5 mr-1" />
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {/* Floating scroll indicator */}
          {showScrollIndicator && (
            <div className="sticky bottom-4 flex justify-center pointer-events-none">
              <button
                onClick={scrollToBottom}
                className="pointer-events-auto w-10 h-10 rounded-full 
                           bg-background/80 backdrop-blur-sm
                           border border-border shadow-lg
                           flex items-center justify-center
                           hover:bg-background transition-colors"
              >
                <ChevronDown className="h-5 w-5 text-primary" />
              </button>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept={acceptString}
          multiple
          onChange={handleFileSelect}
          className="hidden"
        />

        {/* PINNED: Input box (hidden in guided mode) */}
        {!guided && (
          <div className="px-4 py-3 border-t border-border bg-background flex-shrink-0">
            {editingMessageId && !isTransitioning && (
              <div className="flex items-center justify-between mb-2 text-sm text-muted-foreground">
                <span>Editing your response...</span>
                <Button variant="ghost" size="sm" onClick={handleCancelEdit}>
                  Cancel
                </Button>
              </div>
            )}

            <AttachmentChips attachments={attachments} onRemove={removeAttachment} />

            <div className="flex gap-2">
              <Button
                variant="ghost"
                size="icon"
                className="h-10 w-10 shrink-0 text-muted-foreground hover:text-foreground"
                onClick={() => fileInputRef.current?.click()}
                disabled={isTyping || isTransitioning || !conversationId}
                title="Attach a file"
              >
                <Paperclip className="h-4 w-4" />
              </Button>
              <textarea
                ref={inputRef}
                value={input}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                placeholder={
                  isTransitioning
                    ? "Building your prompt..."
                    : editingMessageId
                      ? "Update your response..."
                      : "Type your response..."
                }
                rows={1}
                style={{ minHeight: "40px", maxHeight: "120px" }}
                className="flex-1 resize-none rounded-xl border border-border bg-muted/30 px-4 py-2 text-base focus:outline-none focus:ring-2 focus:ring-primary/50 disabled:opacity-50 overflow-y-auto"
                disabled={isTyping || isTransitioning}
              />
              <Button
                onClick={handleSend}
                disabled={!input.trim() || isTyping || isTransitioning}
                size="icon"
                className="h-auto aspect-square"
              >
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PromptBuilderChat;
